<template>
  <section>
    <div class="container">
      <div class="row mb-2">
        <div class="col-12 d-flex">
          <div class="d-flex mb-auto mt-1">
            <v-icon
              icon="chevron-left"
              class="cursor-pointer"
              @click="() => $router.back()"
            />
          </div>
        </div>
        <div class="col-12 text-center">
          <h1>Vos documents</h1>
          <div class="lead fw-bold mb-1">
            {{ productName }}
          </div>
          <div>N° {{ contract.reference }}</div>
        </div>
        <div class="col-12 mt-3 mb-2 d-flex align-items-center justify-content-center position-relative">
          <ul class="nav nav-pills custom-pills">
            <li class="nav-item">
              <a
                class="nav-link cursor-pointer fw-bold"
                :class="{ active: activeTab === 'CURRENT' }"
                @click="activeTab = 'CURRENT'"
              >
                Période en cours
              </a>
            </li>
            <li class="nav-item ms-2">
              <a
                class="nav-link cursor-pointer fw-bold"
                :class="{ active: activeTab === 'PREVIOUS' }"
                @click="activeTab = 'PREVIOUS'"
              >
                Périodes précédentes
              </a>
            </li>
          </ul>
          <div class="d-flex gap-2 ms-3 filter-dropdowns">
            <v-filter-dropdown
              :value="selectedType"
              :options="typeDropdownOptions"
              placeholder="Type"
              @input="onTypeChange"
            />
            <v-filter-dropdown
              :value="selectedYear"
              :options="yearDropdownOptions"
              placeholder="Année"
              @input="onYearChange"
            />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <transition-group
            name="flip-list"
            :css="false"
            tag="div"
            @before-enter="beforeEnter"
            @enter="enter"
            @leave="leave"
          >
            <v-display
              v-for="document in documentsContainer"
              :key="document.documentId || document.id"
              :document="document"
              class="mb-2"
            />
          </transition-group>
        </div>
        <div class="col-12 text-center">
          <v-loader
            title="Je ne trouve pas un document"
            color="blue-400"
            class="text-white mt-2 mb-3"
            :next="openIntercom"
          />
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import { uniq } from 'lodash-es';
import { mapGetters, mapActions } from 'vuex';

import Velocity from 'velocity-animate';

import DocumentDisplay from '@/components/documents/helpers/DocumentDisplay.vue';
import FilterDropdown from '@/components/common/filters/FilterDropdown.vue';

export default {
  name: 'DocumentByContract',
  components: {
    'v-display': DocumentDisplay,
    'v-filter-dropdown': FilterDropdown,
  },
  data() {
    return {
      documentsContainer: [],
      badges: [
        { key: 'CERTIFICATE', name: 'Attestation' },
        { key: 'INVOICE', name: 'Facture' },
        { key: 'PROJECT', name: 'Devis' },
        { key: 'POLICY', name: 'Contrat' },
        { key: 'AMENDMENT', name: 'Avenant' },
        { key: 'ADVICE', name: 'Conditions générales' },
        { key: '*', name: 'Divers' },
      ],
      asc: true,
      activeTab: 'CURRENT',
      selectedType: null,
      selectedYear: null,
    };
  },
  computed: {
    ...mapGetters({
      documents: 'contracts/certificates',
      contracts: 'contracts/get',
      products: 'rater/products',
    }),
    contractId() {
      return this.$route.params.contractId;
    },
    contractsByProductId() {
      const productId = parseInt(this.$route.params.productId, 10);
      return this.contracts.filter(({ productId: contractProductId }) => contractProductId === productId);
    },
    contract() {
      const exactContract = this.contracts.find(({ id, activeVersionId, policyId }) => (
        [id, activeVersionId, policyId].includes(this.contractId)
      ));
      if (exactContract) return exactContract;
      const contractsWithDocuments = this.contractsByProductId
        .filter(({ documents }) => (documents || []).length > 0);
      if (contractsWithDocuments.length === 1) return contractsWithDocuments[0];
      return {};
    },
    productName() {
      return (this.products.find(({ id }) => id === this.contract.productId) || {}).name;
    },

    // Documents filtered depending productId
    contractIds() {
      const {
        id,
        activeVersionId,
        policyId,
      } = this.contract;
      return uniq([this.contractId, id, activeVersionId, policyId].filter(Boolean));
    },
    documentsByContractId() {
      return this.documents.filter(({
        subscriptionId,
        contractId,
        contractActiveVersionId,
        contractPolicyId,
      }) => [
        subscriptionId,
        contractId,
        contractActiveVersionId,
        contractPolicyId,
      ].some((id) => this.contractIds.includes(id)));
    },
    allYears() {
      const documents = this.documentsByContractId;
      return uniq([
        ...documents.map(({ effectiveDate }) => (new Date(effectiveDate)).getFullYear()),
        ...documents.map(({ endEffectiveDate }) => (new Date(endEffectiveDate)).getFullYear()),
      ]);
    },
    allTypes() {
      const documents = this.documentsByContractId;
      return uniq(documents
        .map((doc) => doc.template?.documentType ?? doc.type)
        .filter(Boolean));
    },
    typeOptions() {
      return this.badges
        .filter(({ key }) => key === '*' || this.allTypes.includes(key));
    },
    typeDropdownOptions() {
      return this.typeOptions.map(({ key, name }) => ({ value: key, label: name }));
    },
    yearDropdownOptions() {
      return this.allYears.map((y) => ({ value: y, label: String(y) }));
    },
  },
  watch: {
    activeTab() {
      this.applyFilterOnDocuments();
    },
    documentsByContractId: {
      handler() {
        this.applyFilterOnDocuments();
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.applyFilterOnDocuments();
  },
  methods: {
    ...mapActions({
      openIntercom: 'intercom/openMessenger',
    }),

    onTypeChange(value) {
      this.selectedType = value;
      this.applyFilterOnDocuments();
    },
    onYearChange(value) {
      this.selectedYear = value;
      this.applyFilterOnDocuments();
    },

    // Sorting Asc/Desc
    sortByDate(documents, isAsc) {
      const shadow = [...documents];
      shadow.sort(({ createdAt: a }, { createdAt: b }) => {
        const aFullYear = (new Date(a)).getTime();
        const bFullYear = (new Date(b)).getTime();
        return isAsc ? (bFullYear - aFullYear) : (aFullYear - bFullYear);
      });
      return shadow;
    },
    applyFilterOnDocuments() {
      const matchedDocuments = this.documentsByContractId;
      const dateFilteredDocuments = matchedDocuments.filter(this.filterByDates);
      const typeFilteredDocuments = dateFilteredDocuments.filter(this.filterByType);

      const documents = typeFilteredDocuments
        .filter((doc) => (this.activeTab === 'CURRENT' ? doc.current : !doc.current));

      this.documentsContainer = this.asc
        ? this.sortByDate(documents, true)
        : this.sortByDate(documents, false);
    },

    // Filters to apply on documents
    filterByType(doc) {
      if (this.selectedType === null) return true;
      const documentType = doc.template?.documentType ?? doc.type;
      if (this.selectedType === '*' && !documentType) return true;
      return documentType === this.selectedType;
    },
    filterByDates({ effectiveDate: a, endEffectiveDate: b }) {
      if (this.selectedYear === null) return true;
      const startYear = (new Date(a)).getFullYear();
      const endYear = (new Date(b)).getFullYear();
      return startYear === this.selectedYear
        || endYear === this.selectedYear;
    },

    // Velocity Functions to transform
    beforeEnter(el) {
      Object.assign(el.style, { opacity: 0 });
      Object.assign(el.style, { height: 0 });
    },
    enter(el, done) {
      const delay = el.dataset.index * 150;
      setTimeout(() => {
        Velocity(
          el,
          { opacity: 1, height: 150 },
          { complete: done },
        );
      }, delay);
    },
    leave(el, done) {
      const delay = el.dataset.index * 150;
      setTimeout(() => {
        Velocity(
          el,
          { opacity: 0, height: 0 },
          { complete: done },
        );
      }, delay);
    },

  },
};
</script>

<style scoped>
.flip-list-move {
  transition: transform 1s;
}
.custom-pills .nav-link {
  color: #3f83f8;
  background-color: transparent;
  border-radius: 9999px;
  padding: 0.5rem 1rem;
}
.custom-pills .nav-link.active {
  color: #fff;
  background-color: #3f83f8;
}

@media screen and (max-width: 767px) {
  .filter-dropdowns {
    position: static !important;
    margin-top: 8px;
    margin-left: 0 !important;
    justify-content: center;
  }
}
</style>
